import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Copy, Check, ExternalLink, Info, AlertCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function SuccessModal({
  isOpen,
  onClose,
  transNo,
  loaNo,
  fromTracker = false,
  onBackToTracker = null,
}) {
  const [copiedField, setCopiedField] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1200);
    } catch {
      console.error("Copy failed");
    }
  };

  const handleConfirmNavigate = () => {
    setShowConfirm(false);
    onClose();
    navigate("/transactions");
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const displayItems = [
    { label: "Transaction No", value: transNo, field: "transNo" },
    loaNo ? { label: "LOA No", value: loaNo, field: "loaNo" } : null,
  ].filter(Boolean);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 flex items-center justify-center z-50"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 text-gray-800"
            >
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="text-emerald-500 h-6 w-6" />
                <h2 className="text-lg font-semibold">Success!</h2>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                Transaction Number generated successfully.
              </p>
              {/* Transaction Info */}
              <div className="space-y-3">
                {displayItems.map((item) => (
                  <div
                    key={item.field}
                    className="flex items-center justify-between bg-gray-50 rounded-lg p-3"
                  >
                    <div className="w-1/3 text-sm font-medium text-gray-600">
                      {item.label}
                    </div>
                    <div className="flex-1 text-right flex items-center justify-end gap-2 text-sm text-gray-900">
                      <span className="break-words">{item.value}</span>
                      <button
                        onClick={() => handleCopy(item.value, item.field)}
                        className={`flex items-center gap-1 px-2 py-1 text-xs rounded border ${
                          copiedField === item.field
                            ? "border-emerald-300 text-emerald-600 bg-emerald-50"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {copiedField === item.field ? (
                          <>
                            <Check className="h-4 w-4" /> Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" /> Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <hr className="my-4" />
              {/* Information Display with Icons */}
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <ArrowRight className="text-blue-500 h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">
                      You can now click <strong>Continue</strong> to proceed with
                      <strong>"Generate LOA"</strong>.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
                  <AlertCircle className="text-yellow-500 h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700">
                      If you made a mistake or need to cancel this transaction, click
                      the <strong>Cancel This TRN</strong> button and provide your reason.
                    </p>
                  </div>
                </div>
              </div>
              <div
                onClick={() => setShowConfirm(true)}
                className="text-sm mt-3 text-emerald-600 flex items-center gap-1 cursor-pointer hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                View in Transactions
              </div>
              <div className="flex justify-end mt-5 gap-2">
                {fromTracker && typeof onBackToTracker === "function" ? (
                  <button
                    onClick={onBackToTracker}
                    className="px-4 py-2 border border-indigo-300 bg-indigo-50 text-indigo-700 font-medium rounded-lg hover:bg-indigo-100"
                  >
                    Back to Tracker
                  </button>
                ) : null}
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
                >
                  OK
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 flex items-center justify-center z-[9999]"
            onClick={handleCancelConfirm}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-sm rounded-2xl shadow-xl p-6 text-gray-800"
            >
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-blue-500 h-6 w-6" />
                <h3 className="text-lg font-semibold">Confirm Navigation</h3>
              </div>
              <p className="text-sm text-gray-700 mb-6">
                You will be redirected to the Transactions page. This will close the current modal.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelConfirm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmNavigate}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700"
                >
                  Go to Transaction List
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
